import React, { useState, useEffect } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsToShow, setItemsToShow] = useState(3);
  const { language, t } = useLanguage();

  // Fallback reviews matching the CMS structure
  const fallbackReviews = [
    {
      id: '1',
      author: { da: 'Joan Nielsen' },
      rating: 5,
      text: { da: '<p>Virkelig god oplevelse. Rigtig god og kompetent hjælp fra start til slut. Kan varmt anbefales at bruge dette firma.</p>' },
      date: '2025-05-21',
      source: { da: 'Google' }
    },
    {
      id: '2',
      author: { da: 'Nae Burlacu' },
      rating: 5,
      text: { da: '<p>Comunicare excelenta,prietenoși,recomand.</p>' },
      date: '2025-04-22',
      source: { da: 'Google' }
    },
    {
      id: '3',
      author: { da: 'Christian Sohl' },
      rating: 5,
      text: { da: '<p>Rigtig fin oplevelse in forbindelse med salg af bil. God kommunikation og hurtig betaling efter aftale. Afhentning efter betaling.<br/>Kan anbefales.</p>' },
      date: '2025-04-04',
      source: { da: 'Google' }
    },
    {
      id: '4',
      author: { da: 'Peter Halling Bruun' },
      rating: 5,
      text: { da: '<p>Ich komme aus Dänemark, und ich habe mein Auto bei Vejle Auto Export gekauft. Es war ein sehr gutes Erlebnis. Sami Kobeita ist einen sehr netter und vertrauenswürdiger Mann, und ich kan nur Positives berichten. 💪🤠</p><p>Alles lief genau nach Vorschrift und ich habe ein sehr gepflegtes Auto gekauft. 🚙😃 Sami bekommt meine wärmsten Empfehlungen! 😎</p><p>Vielen Dank für eine wirklich gute Abwicklung! 🙏 5 Sterne von mir - gerne wieder… 🤩</p>' },
      date: '2025-03-23',
      source: { da: 'Google' }
    },
    {
      id: '5',
      author: { da: 'Mădălin Pirvu' },
      rating: 5,
      text: { da: '<p>Oameni de calitate super buni și servicii de top , recomand cu toată încrederea va respect cu toată stima !</p>' },
      date: '2025-03-22',
      source: { da: 'Google' }
    },
    {
      id: '6',
      author: { da: 'Ionut Tavi' },
      rating: 5,
      text: { da: '<p>I had an excellent experience with this seller! Communication was clear and prompt throughout the entire process. Payment was hassle-free, and they even went above and beyond by helping me load the car. I highly recommend this seller for their fair practices and exceptional service. I will definitely come back for future purchases! Thank you! ♥️🙏🏼</p>' },
      date: '2024-10-23',
      source: { da: 'Google' }
    },
    {
      id: '7',
      author: { da: 'Be4aR' },
      rating: 5,
      text: { da: '<p>Everything went smooth,from communication to payment and and the end they helped me with loading the car. I recommend this seller.</p>' },
      date: '2024-10-23',
      source: { da: 'Google' }
    },
    {
      id: '8',
      author: { da: 'matilde kvist' },
      rating: 5,
      text: { da: '<p>Stor hjælp ved salg af bil, sød, venlig og ærlig</p>' },
      date: '2024-10-12',
      source: { da: 'Google' }
    }
  ];

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/v2/content/reviews');
        if (response.ok) {
          const data = await response.json();
          if (data.items && data.items.length > 0) {
            setReviews(data.items);
          } else {
            setReviews(fallbackReviews);
          }
        } else {
          setReviews(fallbackReviews);
        }
      } catch (error) {
        setReviews(fallbackReviews);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setItemsToShow(1);
      } else if (window.innerWidth < 1024) {
        setItemsToShow(2);
      } else {
        setItemsToShow(3);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset index if itemsToShow changes to prevent empty spaces
  useEffect(() => {
    if (reviews.length > 0) {
      setCurrentIndex((prev) => Math.min(prev, Math.max(0, reviews.length - itemsToShow)));
    }
  }, [itemsToShow, reviews.length]);

  const handlePrev = () => {
    if (reviews.length <= itemsToShow) return;
    setCurrentIndex((prev) => {
      if (prev === 0) {
        return reviews.length - itemsToShow;
      }
      return prev - 1;
    });
  };

  const handleNext = () => {
    if (reviews.length <= itemsToShow) return;
    setCurrentIndex((prev) => {
      if (prev >= reviews.length - itemsToShow) {
        return 0;
      }
      return prev + 1;
    });
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('da-DK', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return dateStr;
    }
  };

  const authorAvatars = {
    "Joan Nielsen": "https://assets.auxodev.dk/assets/5bfde40d-44ae-4b1e-9e89-bf8f3817c44f/8ac2c1ee-0014-4737-a906-543800bc5d34.png",
    "Nae Burlacu": "https://assets.auxodev.dk/assets/5bfde40d-44ae-4b1e-9e89-bf8f3817c44f/51d5c5f1-1c83-4daa-ba96-91ec5d002c1f.png",
    "Christian Sohl": "https://assets.auxodev.dk/assets/5bfde40d-44ae-4b1e-9e89-bf8f3817c44f/a1e40a59-0eff-4395-8555-ca747f94761f.png",
    "Peter Halling Bruun": "https://assets.auxodev.dk/assets/5bfde40d-44ae-4b1e-9e89-bf8f3817c44f/d495e025-0579-4902-9c36-031440725213.png",
    "Mădălin Pirvu": "https://assets.auxodev.dk/assets/5bfde40d-44ae-4b1e-9e89-bf8f3817c44f/0bc167e0-c638-4d6f-bbc6-0fda6a5a881f.png",
    "Ionut Tavi": "https://assets.auxodev.dk/assets/5bfde40d-44ae-4b1e-9e89-bf8f3817c44f/6643683e-9d25-406b-9ca6-7123764e64c5.png",
    "Be4aR": "https://assets.auxodev.dk/assets/5bfde40d-44ae-4b1e-9e89-bf8f3817c44f/c82945b4-2260-4e27-90aa-5f2445907b7b.png",
    "matilde kvist": "https://assets.auxodev.dk/assets/5bfde40d-44ae-4b1e-9e89-bf8f3817c44f/b4eafa97-f84f-4510-9973-df4b7e846c50.png"
  };

  const visibleReviews = reviews.slice(currentIndex, currentIndex + itemsToShow);

  return (
    <section id="anmeldelser" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        
        {/* Header Section with Overall Rating */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10 border-b border-slate-100 pb-6">
          <div>
            <h2 className="text-2xl md:text-3xl font-black italic text-[#293e30] tracking-tight uppercase">
              {t('reviews.title')}
            </h2>
            <p className="text-slate-600 mt-1.5 text-sm max-w-xl">
              {t('reviews.desc')}
            </p>
          </div>

          {/* Google Rating Card */}
          <div className="flex items-center gap-4 shrink-0">
            <div className="text-center">
              <span className="block text-3xl font-extrabold text-slate-900">4,6</span>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Ud af 5</span>
            </div>
            <div className="h-10 w-px bg-slate-200" />
            <div>
              <div className="flex items-center gap-0.5 text-[#f6bb06] mb-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-current" />
                ))}
              </div>
              <p className="text-xs font-bold text-slate-800">
                {language === 'da' ? 'Fremragende på Google' : language === 'de' ? 'Hervorragend auf Google' : 'Excellent on Google'}
              </p>
            </div>
          </div>
        </div>

        {/* Reviews Slider Container */}
        <div className="relative">
          {/* Elfsight Google Reviews | VEJLE AUTO EXPORT */}
          <div className="elfsight-app-ec5a28f8-9e4b-45dc-b5d1-3506052ee111" data-elfsight-app-lazy />

          {/* Navigation Controls */}
          {reviews.length > itemsToShow && (
            <div className="flex justify-between items-center mt-8">
              {/* Left Arrow */}
              <button 
                onClick={handlePrev}
                className="p-2 text-slate-600 hover:text-slate-900 transition-colors cursor-pointer flex items-center justify-center"
                aria-label="Forrige anmeldelser"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>

              {/* Dots Indicator */}
              <div className="flex items-center gap-1.5">
                {Array.from({ length: reviews.length - itemsToShow + 1 }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentIndex(i)}
                    className={`h-1.5 transition-all duration-300 rounded-none ${
                      currentIndex === i ? 'w-6 bg-slate-900' : 'w-1.5 bg-slate-200 hover:bg-slate-400'
                    }`}
                    aria-label={`Gå til slide ${i + 1}`}
                  />
                ))}
              </div>

              {/* Right Arrow */}
              <button 
                onClick={handleNext}
                className="p-2 text-slate-600 hover:text-slate-900 transition-colors cursor-pointer flex items-center justify-center"
                aria-label="Næste anmeldelser"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
